import { createContext, useContext, useState } from 'react';
import exportToGlobalNamespace from '@rsos/utils/exportToGlobalNamespace';

export const FlagContext = createContext({});

export const FLAG_PREFIX = 'FLAG_';
export const RELEASE_FLAG_SCRIPT_ID = 'feature-flags-json';

export const readDevelopmentFlags = () => {
  const flags = {};
  Object.keys(process.env).forEach(key => {
    if (key.startsWith(FLAG_PREFIX)) {
      const value = process.env[key];
      const cleanKey = `${key}`.replace(FLAG_PREFIX, '').toLowerCase();
      if (typeof value === 'string') {
        flags[cleanKey] = value === 'true';
      } else {
        flags[cleanKey] = value;
      }
    }
  });
  return flags;
};

export const readReleaseFlags = () => {
  const script = document.querySelector(`#${RELEASE_FLAG_SCRIPT_ID}`);
  try {
    const flags = {};
    const releaseFlags = JSON.parse(script.textContent);
    Object.keys(releaseFlags).forEach(key => {
      flags[key.toLowerCase()] = releaseFlags[key];
    });
    return flags;
  } catch (error) {
    return {};
  }
};

export const readFlags = () => {
  const devFlags = readDevelopmentFlags();
  const releaseFlags = readReleaseFlags();

  // combine flags, with release flags taking precedence
  const combinedFlags = { ...devFlags, ...releaseFlags };

  // check for flags that are missing in the release flags and explicitly set
  // the value to false in a deployed environment to prevent unexpected early
  // release of features that are still in development
  for (const flagName in combinedFlags) {
    if (!(flagName in releaseFlags)) {
      if (process.env.NODE_ENV === 'development') {
        combinedFlags[flagName] = devFlags[flagName];
      } else {
        combinedFlags[flagName] = false;
      }

      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(
          `[RSOS]: Release flag ${flagName} was found locally but is missing remotely.`,
          `\n`,
          `It will be disabled by default in a deployed environment.`,
          `\n`,
          `If it is a valid flag, please add it to hydra to ensure a smooth deployment.`,
        );
      }
    } else {
      combinedFlags[flagName] = releaseFlags[flagName];
      if (process.env.NODE_ENV === 'development') {
        if (devFlags[flagName] === undefined) {
          // eslint-disable-next-line no-console -- this is a dev warning
          console.warn(
            `[RSOS]: Release flag ${flagName} was found remotely but is missing locally.`,
            `\n`,
            `If it's a valid flag, please add it locally so it can be tracked:`,
            `\n`,
            `\t yarn flags add ${flagName}`,
            `\n`,
            `Otherwise, please clean up the flag in hydra.`,
          );
        }
      }
    }
  }

  return combinedFlags;
};

export const useFlag = name => {
  const flags = useContext(FlagContext);
  return flags[`${name}`.toLowerCase()];
};

export const useFlags = () => {
  return useContext(FlagContext);
};

/**
 * Check for a flag outside the context of a hook.
 *
 * NOTE: This is best limited to on-load behavior; it will not live-update when
 * __RSOS?.flags.override is called in the console. Use the component or the
 * hook whenever possible!
 */
export const isFlagged = name => {
  const flags = window.__RSOS?.flags?.list() || readFlags();
  return flags[name];
};

const FlagProvider = ({ children }) => {
  const [flags, setFlags] = useState(readFlags());
  const { Provider } = FlagContext;

  exportToGlobalNamespace('flags', {
    override: (key, value) => {
      const lowerKey = key.toLowerCase();
      setFlags({ ...flags, [lowerKey]: value });
    },
    list: () => flags,
  });

  return <Provider value={flags}>{children}</Provider>;
};

export default FlagProvider;
